import { extensionUsersResponseSchema, extensionInventoryRequestSchema, extensionInventoryResponseSchema, extensionInventoryDeleteRequestSchema, extensionInventoryDeleteResponseSchema, evaluateExtensionsRequestSchema, evaluateExtensionsResponseSchema, extensionIdSchema, extensionScanResultDetailSchema, extensionScanResultQuerySchema, } from '../contracts/extensions';
import { listPersonalExtensionsDataTableParamsSchema, personalExtensionTableResponseSchema, personalExtensionFilterOptionsQuerySchema, personalExtensionFilterOptionsResponseSchema, } from '../contracts/account-extensions';
import { listOrgExtensionsDataTableParamsSchema, orgExtensionTableResponseSchema, orgExtensionFilterOptionsQuerySchema, orgExtensionFilterOptionsResponseSchema, } from '../contracts/extensions';
import { serializeTableQueryParams } from '../contracts/table';
import { parseSchema } from '../errors';
import { organizationIdSchema, } from '../contracts/organizations';
/**
 * Get users who have a specific extension installed
 *
 * Authorization:
 * - Owner/Manager: Returns all users with this extension
 * - Member: Returns only the requesting user's data
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param extensionId - Extension ID (e.g., "microsoft.python")
 * @returns List of users with this extension installed
 */
export async function getExtensionUsers(client, organizationId, extensionId) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedExtensionId = parseSchema(extensionIdSchema, extensionId);
    const response = await client.get(`/organizations/${validatedId}/extensions/${encodeURIComponent(validatedExtensionId)}/users`);
    return parseSchema(extensionUsersResponseSchema, response);
}
/**
 * Upsert extension inventory for the authenticated user (API key auth)
 */
export async function upsertUserExtensionInventory(client, data) {
    const validatedData = parseSchema(extensionInventoryRequestSchema, data);
    const response = await client.post('/account/extensions', validatedData);
    return parseSchema(extensionInventoryResponseSchema, response);
}
/**
 * Delete an extension from inventory (API key auth)
 */
export async function removeUserExtensionInventory(client, extensionId, data) {
    const validatedExtensionId = parseSchema(extensionIdSchema, extensionId);
    const validatedData = parseSchema(extensionInventoryDeleteRequestSchema, data);
    const response = await client.delete(`/account/extensions/${encodeURIComponent(validatedExtensionId)}`, { data: validatedData });
    return parseSchema(extensionInventoryDeleteResponseSchema, response);
}
/**
 * Evaluate extensions for security risks (agent API key auth)
 */
export async function evaluateExtensions(client, data) {
    const validatedData = parseSchema(evaluateExtensionsRequestSchema, data);
    const response = await client.post('/extensions/evaluate', validatedData);
    return parseSchema(evaluateExtensionsResponseSchema, response);
}
/**
 * List personal extensions with DataTable support (filters, search, sorting).
 *
 * Sends filters using bracket notation via serializeTableQueryParams.
 * The response follows the standard DataTable shape: { data, pagination, meta }.
 *
 * @param client - Axios client instance
 * @param params - DataTable query parameters (pagination, filters, sort, search)
 * @param options - Optional AbortSignal for request cancellation
 * @returns DataTable response with extension rows
 */
export async function listUserExtensionsDataTable(client, params = {}, options) {
    const validatedParams = parseSchema(listPersonalExtensionsDataTableParamsSchema, params);
    const response = await client.get('/account/extensions', {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(personalExtensionTableResponseSchema, response);
}
/**
 * Get distinct filter option values for a personal extension field.
 *
 * @param client - Axios client instance
 * @param params - Field, optional search, limit, offset
 * @returns List of option values and pagination info
 */
export async function getUserExtensionFilterOptions(client, params) {
    const validatedParams = parseSchema(personalExtensionFilterOptionsQuerySchema, params);
    const response = await client.get('/account/extensions/filter-options', {
        params: validatedParams,
    });
    return parseSchema(personalExtensionFilterOptionsResponseSchema, response);
}
/**
 * List organization extensions with DataTable support (filters, search, sorting).
 *
 * Sends filters using bracket notation via serializeTableQueryParams.
 * The response follows the standard DataTable shape: { data, pagination, meta }.
 *
 * NOTE: Default sort (name asc) is enforced by the backend handler, not here.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - DataTable query parameters (pagination, filters, sort, search)
 * @param options - Optional AbortSignal for request cancellation
 * @returns DataTable response with extension rows
 */
export async function listOrgExtensionsDataTable(client, organizationId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(listOrgExtensionsDataTableParamsSchema, params);
    const response = await client.get(`/organizations/${validatedId}/extensions`, {
        params: validatedParams,
        paramsSerializer: serializeTableQueryParams,
        signal: options?.signal,
    });
    return parseSchema(orgExtensionTableResponseSchema, response);
}
/**
 * Get distinct filter option values for an org extension field.
 *
 * @param client - Axios client instance
 * @param organizationId - Organization ID
 * @param params - Field, optional search, limit, offset
 * @returns List of option values and pagination info
 */
export async function getOrgExtensionFilterOptions(client, organizationId, params) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedParams = parseSchema(orgExtensionFilterOptionsQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/extensions/filter-options`, {
        params: validatedParams,
    });
    return parseSchema(orgExtensionFilterOptionsResponseSchema, response);
}
/**
 * Get the latest capability-detection scan result for an organization extension.
 *
 * Authorization matches the org extensions list (member/manager/owner/support;
 * members are scoped to their own findings server-side).
 */
export async function getOrgExtensionScanResult(client, organizationId, extensionId, params = {}, options) {
    const validatedId = parseSchema(organizationIdSchema, organizationId);
    const validatedExtensionId = parseSchema(extensionIdSchema, extensionId);
    const validatedParams = parseSchema(extensionScanResultQuerySchema, params);
    const response = await client.get(`/organizations/${validatedId}/extensions/${encodeURIComponent(validatedExtensionId)}/scan-result`, {
        params: validatedParams,
        signal: options?.signal,
    });
    return parseSchema(extensionScanResultDetailSchema, response);
}
/**
 * Get the latest capability-detection scan result for a personal-account extension.
 */
export async function getAccountExtensionScanResult(client, extensionId, params = {}, options) {
    const validatedExtensionId = parseSchema(extensionIdSchema, extensionId);
    const validatedParams = parseSchema(extensionScanResultQuerySchema, params);
    const response = await client.get(`/account/extensions/${encodeURIComponent(validatedExtensionId)}/scan-result`, {
        params: validatedParams,
        signal: options?.signal,
    });
    return parseSchema(extensionScanResultDetailSchema, response);
}
//# sourceMappingURL=extensions.js.map